// Database Explorer - tables, read-only views and inline table editing
import React, { useState, useEffect, useCallback } from 'react';
import './DatabaseExplorer.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.gboundy.ca';

function DatabaseExplorer() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [tables, setTables] = useState([]);
  const [expandedTable, setExpandedTable] = useState(null);
  const [columns, setColumns] = useState({});
  const [sqlQuery, setSqlQuery] = useState('SELECT * FROM network_devices LIMIT 10');
  const [queryResult, setQueryResult] = useState(null);
  const [resultTableName, setResultTableName] = useState(null);
  const [queryError, setQueryError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tableMetadata, setTableMetadata] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [updateStatus, setUpdateStatus] = useState(null);

  const getToken = () => sessionStorage.getItem('network_discovery_token');

  const fetchTables = async (token) => {
    try {
      const response = await fetch(`${API_BASE}/api/iot/database/tables`, {
        headers: { 'X-Network-Discovery-Token': token || getToken() }
      });
      if (response.ok) {
        const data = await response.json();
        setTables(data);
      }
    } catch (err) {
      console.error('Error fetching tables:', err);
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem('network_discovery_token');
    if (token) {
      setIsAuthenticated(true);
      fetchTables(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/iot/auth-network-discovery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await response.json();
      if (data.success) {
        sessionStorage.setItem('network_discovery_token', data.token);
        setIsAuthenticated(true);
        fetchTables(data.token);
      } else {
        setError(data.error || 'Authentication failed');
      }
    } catch (err) {
      setError('Connection error');
    }
  };

  const fetchColumns = async (tableName) => {
    if (columns[tableName]) {
      setExpandedTable(expandedTable === tableName ? null : tableName);
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/iot/database/tables/${tableName}/columns`, {
        headers: { 'X-Network-Discovery-Token': getToken() }
      });
      if (response.ok) {
        const data = await response.json();
        setColumns(prev => ({ ...prev, [tableName]: data }));
        setExpandedTable(tableName);
      }
    } catch (err) {
      console.error('Error fetching columns:', err);
    }
  };

  const fetchTableMetadata = useCallback(async (tableName) => {
    if (tableMetadata[tableName]) return tableMetadata[tableName];
    try {
      const response = await fetch(`${API_BASE}/api/iot/database/tables/${tableName}/metadata`, {
        headers: { 'X-Network-Discovery-Token': getToken() }
      });
      if (response.ok) {
        const data = await response.json();
        setTableMetadata(prev => ({ ...prev, [tableName]: data }));
        return data;
      }
    } catch (err) {
      console.error('Error fetching table metadata:', err);
    }
    return null;
  }, [tableMetadata]);

  const getTableNameFromQuery = (sql) => {
    const match = sql.match(/\bFROM\s+(?:public\s*\.\s*)?(?:"([a-zA-Z_][a-zA-Z0-9_]*)"|([a-zA-Z_][a-zA-Z0-9_]*))/i);
    return match ? (match[1] || match[2].toLowerCase()) : null;
  };

  const executeQuery = async () => {
    setQueryError('');
    setQueryResult(null);
    setResultTableName(null);
    setEditingCell(null);
    setUpdateStatus(null);
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iot/database/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Network-Discovery-Token': getToken()
        },
        body: JSON.stringify({ sql: sqlQuery })
      });
      const data = await response.json();
      if (response.ok) {
        setQueryResult(data);
        const tableName = getTableNameFromQuery(sqlQuery);
        setResultTableName(tableName);
        if (tableName) {
          fetchTableMetadata(tableName);
        }
      } else {
        setQueryError(data.error || 'Query failed');
      }
    } catch (err) {
      setQueryError('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableClick = (tableName) => {
    setSqlQuery(`SELECT * FROM ${tableName} LIMIT 100`);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('network_discovery_token');
    setIsAuthenticated(false);
    setTables([]);
    setColumns({});
    setQueryResult(null);
    setResultTableName(null);
    setTableMetadata({});
  };

  const isColumnEditable = (columnName) => {
    const meta = tableMetadata[resultTableName];
    if (!meta || meta.isReadOnly || meta.type === 'view') return false;
    return !meta.indexedColumns.includes(columnName);
  };

  const getPrimaryKeyInfo = (row) => {
    const meta = tableMetadata[resultTableName];
    if (!meta || meta.isReadOnly || meta.type === 'view' || meta.primaryKey.length === 0) return null;
    const pkColumn = meta.primaryKey[0];
    return { column: pkColumn, value: row[pkColumn] };
  };

  const startEditing = (rowIndex, columnName, currentValue) => {
    if (!isColumnEditable(columnName)) return;
    setEditingCell({ rowIndex, columnName });
    setEditValue(currentValue === null ? '' : String(currentValue));
    setUpdateStatus(null);
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = async (row) => {
    if (!editingCell) return;
    
    const tableName = resultTableName;
    const pkInfo = getPrimaryKeyInfo(row);
    
    if (!tableName || !pkInfo) {
      setUpdateStatus({ success: false, message: 'Cannot determine table or primary key' });
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/iot/database/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Network-Discovery-Token': getToken()
        },
        body: JSON.stringify({
          tableName,
          primaryKeyColumn: pkInfo.column,
          primaryKeyValue: pkInfo.value,
          column: editingCell.columnName,
          value: editValue === '' ? null : editValue
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setQueryResult(prev => ({
          ...prev,
          rows: prev.rows.map((r, i) => 
            i === editingCell.rowIndex 
              ? { ...r, [editingCell.columnName]: editValue === '' ? null : editValue }
              : r
          )
        }));
        setUpdateStatus({ success: true, message: 'Updated successfully' });
        setEditingCell(null);
      } else {
        setUpdateStatus({ success: false, message: data.error || 'Update failed' });
      }
    } catch (err) {
      setUpdateStatus({ success: false, message: 'Connection error' });
    }
  };

  const handleKeyDown = (e, row) => {
    if (e.key === 'Enter') {
      saveEdit(row);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="db-explorer">
        <div className="login-container">
          <h1>Database Explorer</h1>
          <p>Enter PIN to access database</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              autoFocus
            />
            <button type="submit">Login</button>
          </form>
          {error && <p className="error">{error}</p>}
        </div>
      </div>
    );
  }

  const currentMeta = resultTableName ? tableMetadata[resultTableName] : null;
  const currentObject = tables.find(table => table.name === resultTableName);
  const isReadOnlyResult = currentMeta?.isReadOnly || currentObject?.type === 'view';
  const objectGroups = [
    { title: 'Tables', objects: tables.filter(table => table.type !== 'view') },
    { title: 'Views', objects: tables.filter(table => table.type === 'view') }
  ];

  return (
    <div className="db-explorer">
      <header className="db-header">
        <h1>Database Explorer</h1>
        <div className="header-right">
          <span className="db-name">weather_data</span>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <div className="db-content">
        <div className="tables-panel">
          {objectGroups.map(group => (
            <section className="object-group" key={group.title} aria-label={group.title}>
              <h2>{group.title} ({group.objects.length})</h2>
              {group.objects.length === 0 && <p className="empty-objects">No {group.title.toLowerCase()} found.</p>}
              <ul className="table-list">
                {group.objects.map(table => (
                  <li key={table.name} className="table-item">
                    <div className="table-header" onClick={() => fetchColumns(table.name)}>
                      <input
                        type="checkbox"
                        aria-label={`Show columns for ${table.name}`}
                        checked={expandedTable === table.name}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => fetchColumns(table.name)}
                      />
                      <button
                        type="button"
                        className="table-name"
                        onClick={(e) => { e.stopPropagation(); handleTableClick(table.name); }}
                        title="Click to generate SELECT query"
                      >
                        {table.name}
                      </button>
                      {table.type === 'view' && <span className="view-badge">VIEW</span>}
                      <span className="table-meta">
                        {table.type === 'view' ? 'Read-only' : table.size} | {table.column_count} cols
                      </span>
                    </div>
                    {expandedTable === table.name && columns[table.name] && (
                      <ul className="column-list">
                        {columns[table.name].map(col => (
                          <li key={col.column_name} className="column-item">
                            <span className="col-name">{col.column_name}</span>
                            <span className="col-type">
                              {col.data_type}
                              {col.character_maximum_length && `(${col.character_maximum_length})`}
                            </span>
                            {col.is_nullable === 'NO' && <span className="col-notnull">NOT NULL</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="query-panel">
          <div className="query-input-section">
            <h2>SQL Query (SELECT only)</h2>
            <textarea
              value={sqlQuery}
              onChange={(e) => setSqlQuery(e.target.value)}
              placeholder="Enter SELECT query..."
              rows={5}
            />
            <div className="query-actions">
              <button onClick={executeQuery} disabled={isLoading}>
                {isLoading ? 'Running...' : 'Execute'}
              </button>
              <button onClick={() => setSqlQuery('')} className="clear-btn">Clear</button>
            </div>
          </div>

          {queryError && (
            <div className="query-error">
              <strong>Error:</strong> {queryError}
            </div>
          )}

          {updateStatus && (
            <div className={`update-status ${updateStatus.success ? 'success' : 'error'}`}>
              {updateStatus.message}
            </div>
          )}

          {queryResult && (
            <div className="query-result-section">
              <div className="result-meta">
                <span>{queryResult.rowCount} rows</span>
                <span>{queryResult.duration}ms</span>
                {isReadOnlyResult ? (
                  <span className="readonly-hint">Read-only view</span>
                ) : currentMeta && currentMeta.primaryKey.length > 0 && (
                  <span className="edit-hint">
                    Click editable cells to edit (non-indexed columns only)
                  </span>
                )}
              </div>
              <div className="result-table-container">
                <table className="result-table">
                  <thead>
                    <tr>
                      {queryResult.fields.map((field, i) => (
                        <th key={i} className={isReadOnlyResult ? 'readonly-col' : currentMeta && currentMeta.indexedColumns.includes(field.name) ? 'indexed-col' : 'editable-col'}>
                          {field.name}
                          {currentMeta && currentMeta.primaryKey.includes(field.name) && <span className="pk-badge">PK</span>}
                          {currentMeta && currentMeta.indexedColumns.includes(field.name) && !currentMeta.primaryKey.includes(field.name) && <span className="idx-badge">IDX</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {queryResult.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {queryResult.fields.map((field, colIndex) => {
                          const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnName === field.name;
                          const editable = isColumnEditable(field.name) && getPrimaryKeyInfo(row);
                          
                          return (
                            <td 
                              key={colIndex}
                              className={`${editable ? 'editable' : 'readonly'} ${isEditing ? 'editing' : ''}`}
                              onClick={() => editable && !isEditing && startEditing(rowIndex, field.name, row[field.name])}
                            >
                              {isEditing ? (
                                <div className="edit-cell">
                                  <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(e, row)}
                                    autoFocus
                                  />
                                  <div className="edit-actions">
                                    <button onClick={() => saveEdit(row)} className="save-btn">✓</button>
                                    <button onClick={cancelEditing} className="cancel-btn">✗</button>
                                  </div>
                                </div>
                              ) : (
                                row[field.name] === null ? <span className="null-value">NULL</span> : 
                                typeof row[field.name] === 'object' ? JSON.stringify(row[field.name]) :
                                String(row[field.name])
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DatabaseExplorer;
